import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'
import LanguageDetector from 'i18next-browser-languagedetector'

// Translation resources with proper nested structure
const resources = {
  en: {
    translation: {
      common: {
        nav: {
          login: 'Login',
          resources: 'Resources',
          emergencyServices: 'Emergency Services',
          guides: 'Guides',
          about: 'About Us',
          contact: 'Contact',
          needHelp: 'Need Help',
          waterDamage: 'Water Damage',
          fireDamage: 'Fire Damage',
          moldRemediation: 'Mold Remediation',
          insuranceGuide: 'Insurance Guide',
          freeGuides: 'Free Guides',
          forProfessionals: 'For Professionals',
          howItWorks: 'How It Works',
          joinNetwork: 'Join Network',
          dashboard: 'Dashboard',
          company: 'Company',
          tagline: "Inland Empire's Restoration Connection",
          smartMatching: 'Smart Matching - Connect Fast',
          forHomeowners: 'For Homeowners',
          home: 'Home',
          services: 'Services',
          findContractors: 'Find Contractors',
          waterDamageRestoration: 'Water Damage Restoration',
          fireDamageRestoration: 'Fire Damage Restoration',
          moldDamageRestoration: 'Mold Damage Restoration',
          contentsRestoration: 'Contents Restoration',
          smokeOdorRemoval: 'Smoke & Odor Removal',
          restorationProfessionals: 'Restoration Professionals',
          forAllProfessionals: 'For All Restoration Professionals',
          whyListWithUs: 'Why List With Us',
          pricingROI: 'Pricing & ROI',
          memberResources: 'Member Resources',
          joinFree: 'Join Free',
          memberAccess: 'Member Access',
          blog: 'Blog',
          customerReviews: 'Customer Reviews',
          getHelp: 'Get Help Now'
        },
        buttons: {
          requestService: 'Request Service',
          emergency: 'Emergency Help'
        },
        footer: {
          tagline: "Inland Empire's #1 Restoration Network",
          sections: {
            homeowners: {
              title: 'For Homeowners',
              getHelp: '🚨 Get Help Now',
              howItWorks: 'How It Works',
              findContractors: 'Find Contractors',
              resources: 'Resources',
              guides: 'Free Guides'
            },
            contractors: {
              title: 'For Contractors',
              whyList: 'Why List With Us',
              joinFree: 'Join Free',
              premium: 'Premium Membership',
              verification: 'Verification Process',
              dashboard: 'Dashboard'
            },
            company: {
              title: 'Company',
              about: 'About Us',
              contact: 'Contact',
              blog: 'Blog',
              privacy: 'Privacy Policy',
              terms: 'Terms of Service'
            }
          },
          bottomBar: {
            copyright: '© 2025 Flood & Fire Pros. All rights reserved.',
            privacy: 'Privacy',
            terms: 'Terms',
            sitemap: 'Sitemap'
          }
        }
      },
      home: {
        seo: {
          title: '24/7 Emergency Water & Fire Damage Restoration | Flood & Fire Pros',
          description: 'Connect with verified local restoration professionals in Los Angeles County & Inland Empire. No platform fees, 24/7 emergency response, licensed specialists.',
          keywords: 'water damage restoration, fire damage restoration, emergency restoration, flood cleanup, Los Angeles restoration, Inland Empire restoration'
        },
        hero: {
          title: 'Southern California Emergency Restoration Services',
          subtitle: 'Connect with Licensed Water, Fire & Mold Restoration Professionals',
          cta: 'Find Restoration Help Now',
          searchPlaceholder: 'Enter your ZIP code for local help'
        },
        benefits: {
          freeService: 'No platform fees - No obligation',
          directContact: 'Professionals contact you directly',
          available247: 'Available 24/7 for emergencies'
        },
        trust: {
          residential: 'Residential & Commercial',
          insurance: 'Insurance Partners',
          emergency: {
            title: '24/7 Emergency'
          },
          verified: 'Verified Professionals'
        },
        emergencyServices: {
          title: '24/7 Emergency Restoration Services',
          water: {
            title: 'Water Damage Restoration',
            description: '24/7 emergency water extraction, structural drying, and complete restoration for floods, leaks, and burst pipes.',
            cta: '→ Find Water Damage Specialists'
          },
          fire: {
            title: 'Fire Damage Restoration',
            description: 'Professional fire and smoke damage cleanup, soot removal, and structural restoration by certified specialists.',
            cta: '→ Find Fire Damage Specialists'
          },
          mold: {
            title: 'Mold Remediation',
            description: 'Safe mold inspection, testing, removal and prevention to protect your health and property from toxic mold.',
            cta: '→ Find Mold Specialists'
          },
          emergency: {
            title: 'Emergency Response',
            description: 'Immediate 24/7 emergency response for any restoration crisis. Get help within minutes, day or night.',
            cta: '→ Get Emergency Help Now'
          }
        },
        whyChoose: {
          title: "Why Choose Southern California's Trusted Restoration Network?",
          certified: {
            title: 'Certified Professionals',
            description: 'All restoration specialists are verified for proper licensing, insurance, and IICRC certifications.'
          },
          free: {
            title: 'Free Platform Access',
            description: 'No fees to connect with contractors. Insurance typically covers restoration work.'
          },
          instant: {
            title: 'Instant 24/7 Response',
            description: "Water damage can't wait. Get connected with available professionals in minutes."
          },
          local: {
            title: 'Truly Local Service',
            description: 'We connect you with restoration professionals in your immediate area who understand local building codes and insurance requirements.'
          }
        },
        testimonialSection: {
          title: 'Trusted by Thousands of Homeowners'
        },
        testimonial: {
          quote: '"When our home flooded at 2 AM, Flood & Fire Pros connected us with a restoration team within 15 minutes. They arrived within the hour and saved our hardwood floors!"',
          author: 'Sarah M., Beverly Hills',
          type: 'Verified Customer • Water Damage Restoration'
        },
        testimonial2: {
          quote: '"Instant matching that works! Connected with three contractors within minutes. Chose the best one for our water damage restoration."',
          author: 'Jennifer L., Corona'
        },
        testimonial3: {
          quote: '"Fire damaged our kitchen, but the team they connected us with handled everything professionally. Insurance paperwork, cleanup, everything."',
          author: 'Michael R., Riverside'
        },
        testimonial4: {
          quote: '"Mold problem in our bathroom was solved within 2 days. The contractor was professional, clean, and explained everything clearly."',
          author: 'Lisa K., San Bernardino'
        },
        testimonial5: {
          quote: '"After the pipe burst in our basement, we were panicking. Flood & Fire Pros connected us instantly with a local team who saved our home."',
          author: 'David & Maria T., Fontana'
        },
        testimonial6: {
          quote: '"Professional service from start to finish. The restoration team was knowledgeable and kept us informed throughout the entire process."',
          author: 'Robert J., Ontario'
        },
        finalCta: {
          title: 'Water Damage Emergency? Connect with Specialists Now!',
          description: 'Every minute counts when dealing with water or fire damage. Connect with verified restoration professionals immediately.',
          button: '🚨 Connect with Restoration Specialists →'
        }
      }
    }
  },
  'es-ca': {
    translation: {
      common: {
        nav: {
          login: 'Entrar',
          resources: 'Recursos',
          emergencyServices: 'Servicios de Emergencia',
          guides: 'Guías',
          about: 'Sobre Nosotros',
          contact: 'Contacto',
          needHelp: '¿Necesitas Ayuda?',
          waterDamage: 'Daños de Agua',
          fireDamage: 'Daños de Incendio',
          moldRemediation: 'Eliminación de Moho',
          insuranceGuide: 'Guía de Seguros',
          freeGuides: 'Guías Gratis',
          forProfessionals: 'Para Profesionales',
          howItWorks: 'Cómo Funciona',
          joinNetwork: 'Únete a la Red',
          dashboard: 'Panel',
          company: 'Empresa',
          tagline: 'Conexión de Restauración del Inland Empire',
          smartMatching: 'Conexión Inteligente - Conéctate Rápido',
          forHomeowners: 'Para Dueños de Casa',
          home: 'Inicio',
          services: 'Servicios',
          findContractors: 'Buscar Contratistas',
          waterDamageRestoration: 'Reparación de Daños de Agua',
          fireDamageRestoration: 'Reparación de Daños de Incendio',
          moldDamageRestoration: 'Eliminación de Moho',
          contentsRestoration: 'Restauración de Pertenencias',
          smokeOdorRemoval: 'Quitar Humo y Malos Olores',
          restorationProfessionals: 'Expertos en Restauración',
          forAllProfessionals: 'Para Todos los Profesionales',
          whyListWithUs: 'Por Qué Anotarte Con Nosotros',
          pricingROI: 'Precios y Ganancia',
          memberResources: 'Recursos para Miembros',
          joinFree: 'Únete Gratis',
          memberAccess: 'Acceso de Miembros',
          blog: 'Blog',
          customerReviews: 'Reviews de Clientes',
          getHelp: 'Conseguir Ayuda Ya'
        },
        buttons: {
          requestService: 'Pedir Servicio',
          emergency: 'Ayuda de Emergencia'
        },
        footer: {
          tagline: 'Red de Restauración #1 del Inland Empire',
          sections: {
            homeowners: {
              title: 'Para Dueños de Casa',
              getHelp: '🚨 Consigue Ayuda Ya',
              howItWorks: 'Cómo Funciona',
              findContractors: 'Buscar Contratistas',
              resources: 'Recursos',
              guides: 'Guías Gratis'
            },
            contractors: {
              title: 'Para Contratistas',
              whyList: 'Por Qué Anotarte Con Nosotros',
              joinFree: 'Únete Gratis',
              premium: 'Membresía Premium',
              verification: 'Proceso de Verificación',
              dashboard: 'Panel de Control'
            },
            company: {
              title: 'La Empresa',
              about: 'Sobre Nosotros',
              contact: 'Contacto',
              blog: 'Blog',
              privacy: 'Privacidad',
              terms: 'Términos de Servicio'
            }
          },
          bottomBar: {
            copyright: '© 2025 Flood & Fire Pros. Todos los derechos reservados.',
            privacy: 'Privacidad',
            terms: 'Términos',
            sitemap: 'Mapa del sitio'
          }
        }
      },
      home: {
        seo: {
          title: 'Servicios de Restauración de Emergencia 24/7 | Flood & Fire Pros',
          description: 'Conéctate con expertos locales en restauración verificados en Los Angeles e Inland Empire. Sin tarifas de plataforma, respuesta 24/7, especialistas con licencia.',
          keywords: 'reparación daños agua, reparación daños incendio, restauración emergencia, limpieza inundaciones, restauración Los Angeles, restauración Inland Empire'
        },
        hero: {
          title: 'Servicios de Restauración de Emergencia en el Sur de California',
          subtitle: 'Conéctate con Expertos Licenciados en Agua, Incendio y Moho',
          cta: 'Buscar Ayuda de Restauración Ya',
          searchPlaceholder: 'Pon tu código postal para ayuda local'
        },
        benefits: {
          freeService: 'Sin tarifas - Sin compromiso',
          directContact: 'Los expertos te contactan directamente',
          available247: 'Disponible 24/7 para emergencias'
        },
        trust: {
          residential: 'Residencial y Comercial',
          insurance: 'Partners de Seguro',
          emergency: {
            title: 'Emergencia 24/7'
          },
          verified: 'Expertos Verificados'
        },
        emergencyServices: {
          title: 'Servicios de Restauración 24/7',
          water: {
            title: 'Reparación de Daños de Agua',
            description: 'Extracción de agua 24/7, secado completo y reparación total para inundaciones, goteras y tuberías rotas.',
            cta: '→ Buscar Expertos en Agua'
          },
          fire: {
            title: 'Reparación de Daños de Incendio',
            description: 'Limpieza profesional de incendio y humo, quitar hollín y reparación de estructura por expertos certificados.',
            cta: '→ Buscar Expertos en Incendios'
          },
          mold: {
            title: 'Eliminación de Moho',
            description: 'Inspección segura, pruebas, eliminación y prevención de moho para proteger tu salud y tu casa del moho tóxico.',
            cta: '→ Buscar Expertos en Moho'
          },
          emergency: {
            title: 'Respuesta de Emergencia',
            description: 'Respuesta inmediata 24/7 para cualquier crisis de restauración. Consigue ayuda en minutos, día o noche.',
            cta: '→ Conseguir Ayuda de Emergencia Ya'
          }
        },
        whyChoose: {
          title: '¿Por Qué Escoger la Red de Restauración Confiable del Sur de California?',
          certified: {
            title: 'Expertos Certificados',
            description: 'Todos nuestros expertos en restauración están verificados con licencias, seguro y certificaciones IICRC.'
          },
          free: {
            title: 'Acceso Gratuito a la Plataforma',
            description: 'Sin tarifas para conectar con contratistas. El seguro generalmente cubre el trabajo de restauración.'
          },
          instant: {
            title: 'Respuesta Rápida 24/7',
            description: 'Los daños de agua no pueden esperar. Conéctate con expertos disponibles en minutos.'
          },
          local: {
            title: 'Servicio Local de Verdad',
            description: 'Te conectamos con expertos en restauración en tu área que conocen los códigos locales y requisitos de seguro.'
          }
        },
        testimonialSection: {
          title: 'Miles de Dueños de Casa Confían en Nosotros'
        },
        testimonial: {
          quote: '"Cuando se inundó nuestra casa a las 2 de la mañana, Flood & Fire Pros nos conectó con un equipo en 15 minutos. ¡Llegaron en una hora y salvaron nuestros pisos de madera!"',
          author: 'Sarah M., Beverly Hills',
          type: 'Cliente Verificado • Reparación de Daños de Agua'
        },
        testimonial2: {
          quote: '"¡Conexión rápida que de verdad funciona! Me conectaron con tres contratistas en minutos. Escogí el mejor para reparar los daños de agua en nuestra casa."',
          author: 'Jennifer L., Corona'
        },
        testimonial3: {
          quote: '"El incendio dañó nuestra cocina, pero el equipo que nos conectaron se encargó de todo profesionalmente. Los papeles del seguro, la limpieza, todo."',
          author: 'Michael R., Riverside'
        },
        testimonial4: {
          quote: '"El problema de moho en nuestro baño se arregló en 2 días. El contratista fue súper profesional, trabajó limpio, y nos explicó todo clarito."',
          author: 'Lisa K., San Bernardino'
        },
        testimonial5: {
          quote: '"Después de que se reventó la tubería en nuestro sótano, estábamos desesperados. Flood & Fire Pros nos conectó al toque con un equipo local que salvó nuestra casa."',
          author: 'David & Maria T., Fontana'
        },
        testimonial6: {
          quote: '"Servicio profesional de principio a fin. El equipo de restauración sabía lo que hacía y nos mantuvo informados todo el tiempo."',
          author: 'Robert J., Ontario'
        },
        finalCta: {
          title: '¿Emergencia de Agua? ¡Conéctate con Expertos Ya!',
          description: 'Cada minuto cuenta con daños de agua o incendio. Conéctate con expertos verificados inmediatamente.',
          button: '🚨 Conectar con Expertos en Restauración →'
        }
      }
    }
  },
  'es-us': {
    translation: {
      common: {
        nav: {
          login: 'Iniciar Sesión',
          resources: 'Recursos',
          emergencyServices: 'Servicios de Emergencia',
          guides: 'Guías',
          about: 'Acerca de Nosotros',
          contact: 'Contacto',
          needHelp: 'Necesita Ayuda',
          waterDamage: 'Daño por Agua',
          fireDamage: 'Daño por Fuego',
          moldRemediation: 'Remediación de Moho',
          insuranceGuide: 'Guía de Seguros',
          freeGuides: 'Guías Gratuitas',
          forProfessionals: 'Para Profesionales',
          howItWorks: 'Cómo Funciona',
          joinNetwork: 'Unirse a la Red',
          dashboard: 'Panel',
          company: 'Empresa',
          tagline: 'Conexión de Restauración del Inland Empire',
          smartMatching: 'Coincidencia Inteligente - Conéctate Rápido',
          forHomeowners: 'Para Propietarios de Vivienda',
          home: 'Inicio',
          services: 'Servicios',
          findContractors: 'Encontrar Contratistas',
          waterDamageRestoration: 'Restauración de Daños por Agua',
          fireDamageRestoration: 'Restauración de Daños por Incendio',
          moldDamageRestoration: 'Restauración de Daños por Moho',
          contentsRestoration: 'Restauración de Contenidos',
          smokeOdorRemoval: 'Eliminación de Humo y Olores',
          restorationProfessionals: 'Profesionales de Restauración',
          forAllProfessionals: 'Para Todos los Profesionales de Restauración',
          whyListWithUs: 'Por Qué Aparecer Con Nosotros',
          pricingROI: 'Precios y ROI',
          memberResources: 'Recursos para Miembros',
          joinFree: 'Únete Gratis',
          memberAccess: 'Acceso de Miembros',
          blog: 'Blog',
          customerReviews: 'Reseñas de Clientes',
          getHelp: 'Obtener Ayuda Ahora'
        },
        buttons: {
          requestService: 'Solicitar Servicio',
          emergency: 'Ayuda de Emergencia'
        },
        footer: {
          tagline: 'Red de Restauración #1 del Inland Empire',
          sections: {
            homeowners: {
              title: 'Para Propietarios de Vivienda',
              getHelp: '🚨 Obtener Ayuda Ahora',
              howItWorks: 'Cómo Funciona',
              findContractors: 'Encontrar Contratistas',
              resources: 'Recursos',
              guides: 'Guías Gratuitas'
            },
            contractors: {
              title: 'Para Contratistas',
              whyList: 'Por Qué Listarse Con Nosotros',
              joinFree: 'Unirse Gratis',
              premium: 'Membresía Premium',
              verification: 'Proceso de Verificación',
              dashboard: 'Panel de Control'
            },
            company: {
              title: 'Empresa',
              about: 'Acerca de Nosotros',
              contact: 'Contacto',
              blog: 'Blog',
              privacy: 'Política de Privacidad',
              terms: 'Términos de Servicio'
            }
          },
          bottomBar: {
            copyright: '© 2025 Flood & Fire Pros. Todos los derechos reservados.',
            privacy: 'Privacidad',
            terms: 'Términos',
            sitemap: 'Mapa del sitio'
          }
        }
      },
      home: {
        seo: {
          title: 'Servicios de Restauración de Emergencia 24/7 | Flood & Fire Pros',
          description: 'Conectese con profesionales de restauración locales verificados en el Condado de Los Angeles e Inland Empire. Sin tarifas de plataforma, respuesta de emergencia 24/7, especialistas licenciados.',
          keywords: 'restauración de daños por agua, restauración de daños por incendio, restauración de emergencia, limpieza de inundaciones, restauración Los Angeles, restauración Inland Empire'
        },
        hero: {
          title: 'Servicios de Restauración de Emergencia de California del Sur',
          subtitle: 'Conectese con Profesionales Licenciados de Restauración de Agua, Incendio y Moho',
          cta: 'Encontrar Ayuda de Restauración Ahora',
          searchPlaceholder: 'Ingrese su código postal para ayuda en su área'
        },
        benefits: {
          freeService: 'Sin tarifas - Sin obligación',
          directContact: 'Los profesionales se contactan directamente contigo',
          available247: 'Disponible 24/7 para emergencias'
        },
        trust: {
          residential: 'Residencial y Comercial',
          insurance: 'Socios de Seguros',
          emergency: {
            title: 'Emergencia 24/7'
          },
          verified: 'Profesionales Verificados'
        },
        emergencyServices: {
          title: 'Servicios de Restauración de Emergencia 24/7',
          water: {
            title: 'Restauración de Daños por Agua',
            description: 'Extracción de agua de emergencia 24/7, secado estructural y restauración completa para inundaciones, fugas y tuberías rotas.',
            cta: '→ Encontrar Especialistas en Daños por Agua'
          },
          fire: {
            title: 'Restauración de Daños por Incendio',
            description: 'Limpieza profesional de daños por incendio y humo, eliminación de hollín y restauración estructural por especialistas certificados.',
            cta: '→ Encontrar Especialistas en Daños por Incendio'
          },
          mold: {
            title: 'Remediación de Moho',
            description: 'Inspección, pruebas, eliminación y prevención de moho segura para proteger su salud y propiedad del moho tóxico.',
            cta: '→ Encontrar Especialistas en Moho'
          },
          emergency: {
            title: 'Respuesta de Emergencia',
            description: 'Respuesta inmediata de emergencia 24/7 para cualquier crisis de restauración. Obtenga ayuda en minutos, día o noche.',
            cta: '→ Obtener Ayuda de Emergencia Ahora'
          }
        },
        whyChoose: {
          title: '¿Por Qué Elegir la Red de Restauración Confiable de California del Sur?',
          certified: {
            title: 'Profesionales Certificados',
            description: 'Todos los especialistas en restauración están verificados con licencias apropiadas, seguro y certificaciones IICRC.'
          },
          free: {
            title: 'Acceso Gratuito a la Plataforma',
            description: 'Sin tarifas para conectar con contratistas. El seguro generalmente cubre el trabajo de restauración.'
          },
          instant: {
            title: 'Respuesta Instantánea 24/7',
            description: 'Los daños por agua no pueden esperar. Conectese con profesionales disponibles en minutos.'
          },
          local: {
            title: 'Servicio Verdaderamente Local',
            description: 'Lo conectamos con profesionales de restauración en su área inmediata que entienden los códigos de construcción locales y requisitos de seguro.'
          }
        },
        testimonialSection: {
          title: 'Miles de Propietarios de Vivienda Confían en Nosotros'
        },
        testimonial: {
          quote: '"Cuando nuestra casa se inundó a las 2 de la mañana, Flood & Fire Pros nos conectó con un equipo de restauración en 15 minutos. ¡Llegaron en una hora y salvaron nuestros pisos de madera!"',
          author: 'Sarah M., Beverly Hills',
          type: 'Cliente Verificado • Restauración de Daños por Agua'
        },
        testimonial2: {
          quote: '"¡Conexión rápida que de verdad funciona! Me conectaron con tres contratistas en minutos. Elegí el mejor para la restauración de daños por agua en nuestra casa."',
          author: 'Jennifer L., Corona'
        },
        testimonial3: {
          quote: '"El incendio dañó nuestra cocina, pero el equipo que nos conectaron manejó todo profesionalmente. Los papeles del seguro, la limpieza, todo."',
          author: 'Michael R., Riverside'
        },
        testimonial4: {
          quote: '"El problema de moho en nuestro baño se resolvió en 2 días. El contratista fue muy profesional, trabajó limpio, y nos explicó todo claramente."',
          author: 'Lisa K., San Bernardino'
        },
        testimonial5: {
          quote: '"Después de que se reventó la tubería en nuestro sótano, estábamos desesperados. Flood & Fire Pros nos conectó al instante con un equipo local que salvó nuestra casa."',
          author: 'David & Maria T., Fontana'
        },
        testimonial6: {
          quote: '"Servicio profesional de principio a fin. El equipo de restauración era muy capacitado y nos mantuvo informados durante todo el proceso."',
          author: 'Robert J., Ontario'
        },
        finalCta: {
          title: '¿Emergencia de Daños por Agua? ¡Conectese con Especialistas Ahora!',
          description: 'Cada minuto cuenta cuando se trata de daños por agua o incendio. Conectese con profesionales de restauración verificados inmediatamente.',
          button: '🚨 Conectar con Especialistas en Restauración →'
        }
      }
    }
  }
}

// Enhanced language detection for Southern California Spanish speakers
const detectionOptions = {
  // Detection order
  order: ['querystring', 'localStorage', 'navigator', 'htmlTag'],

  // Query string parameter
  lookupQuerystring: 'lang',

  // Local storage key
  lookupLocalStorage: 'ffp-language',

  // Cache user language preference
  caches: ['localStorage'],

  // Check for Spanish variants
  checkWhitelist: true,

  // Fallback language
  fallbackLng: 'en',

  // Convert browser language codes to our supported languages
  convertDetectedLanguage: (lng) => {
    // Handle Spanish variants for Southern California
    if (lng && lng.toLowerCase().startsWith('es')) {
      // Detect California Spanish preferences
      if (lng.toLowerCase().includes('mx') || lng.toLowerCase().includes('ca')) {
        return 'es-ca' // California Spanish (Mexican influenced)
      }
      // Default to American Spanish for other US variants
      if (lng.toLowerCase().includes('us')) {
        return 'es-us' // American Spanish
      }
      return 'es-ca' // Default to California Spanish for any other Spanish
    }
    return lng
  }
}

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,

    // Language detection
    detection: detectionOptions,

    // Fallback settings
    fallbackLng: 'en',
    lng: undefined, // Let detector determine language

    // Interpolation
    interpolation: {
      escapeValue: false // React already escapes
    },

    // React specific
    react: {
      useSuspense: false // Avoid suspense issues
    },

    // Error handling
    saveMissing: false,
    missingKeyHandler: (lng, ns, key) => {
      if (process.env.NODE_ENV === 'development') {

      }
    },

    // Debug mode (disable in production)
    debug: true
  })
  .catch((error) => {

  })

export default i18n